import { request, requestVoid } from './api-client';
import {
  ArmSchema,
  CampaignLinkSchema,
  CampaignReportSchema,
  PreSendChecklistSchema,
  CampaignDetailSchema,
  CampaignSummarySchema,
  LintResponseSchema,
  VersionDetailSchema,
  VersionSummarySchema,
  pagedSchema,
  type CampaignReport,
  type CampaignStatus,
  type PreSendChecklist,
} from './schemas';
import { z } from 'zod';

/**
 * One function per endpoint, each naming the schema its response is parsed through. Thin on
 * purpose — the hooks in `hooks/` add caching and the components add rendering; putting any of
 * that here would make the API surface hard to read against the controller.
 */

const CampaignPageSchema = pagedSchema(CampaignSummarySchema);

export async function listCampaigns(params: {
  status?: CampaignStatus;
  query?: string;
  take?: number;
  skip?: number;
}): Promise<z.infer<typeof CampaignPageSchema>> {
  const search = new URLSearchParams();

  if (params.status !== undefined) search.set('status', params.status);
  if (params.query !== undefined && params.query.length > 0) search.set('query', params.query);
  search.set('take', String(params.take ?? 50));
  search.set('skip', String(params.skip ?? 0));

  return request(`/api/v1/campaigns?${search.toString()}`, CampaignPageSchema);
}

export async function getCampaign(campaignId: string) {
  return request(`/api/v1/campaigns/${campaignId}`, CampaignDetailSchema);
}

export async function createCampaign(body: {
  brandId: string;
  name: string;
  subject: string;
  html?: string;
}) {
  return request('/api/v1/campaigns', CampaignDetailSchema, { method: 'POST', body });
}

export async function saveContent(
  campaignId: string,
  armId: string,
  body: { html: string; subject: string; preheader: string | null; message: string | null },
) {
  return request(`/api/v1/campaigns/${campaignId}/arms/${armId}/content`, ArmSchema, {
    method: 'PUT',
    body,
  });
}

/**
 * Build, compile, lint and preview in one request — matching the single endpoint on the API.
 *
 * One call rather than three is what keeps the issues drawer, the preview iframe and the
 * pre-send estimate from disagreeing about the same content.
 */
export async function lintArm(
  campaignId: string,
  armId: string,
  previewContactId?: string,
  signal?: AbortSignal,
) {
  const search = previewContactId === undefined ? '' : `?previewContactId=${previewContactId}`;

  return request(
    `/api/v1/campaigns/${campaignId}/arms/${armId}/lint${search}`,
    LintResponseSchema,
    signal ? { signal } : {},
  );
}

export async function listVersions(campaignId: string, armId: string) {
  return request(
    `/api/v1/campaigns/${campaignId}/arms/${armId}/versions?take=50`,
    z.array(VersionSummarySchema),
  );
}

export async function getVersion(campaignId: string, versionId: string) {
  return request(`/api/v1/campaigns/${campaignId}/versions/${versionId}`, VersionDetailSchema);
}

export async function restoreVersion(campaignId: string, versionId: string) {
  return request(`/api/v1/campaigns/${campaignId}/versions/${versionId}/restore`, ArmSchema, {
    method: 'POST',
  });
}

export async function cloneCampaign(campaignId: string, name: string) {
  return request(`/api/v1/campaigns/${campaignId}/clone`, CampaignDetailSchema, {
    method: 'POST',
    body: { name },
  });
}

// ── Setup: who it goes to, and who it comes from ─────────────────────────────
//
// Both of these block the pre-send checklist while unset, so they are not optional configuration —
// they are steps.

export async function setAudience(
  campaignId: string,
  body: { segmentId: string; excludeSegmentId: string | null; topicId: string | null },
) {
  return request(`/api/v1/campaigns/${campaignId}/audience`, CampaignDetailSchema, {
    method: 'PUT',
    body,
  });
}

export async function setSender(campaignId: string, senderIdentityId: string) {
  return request(`/api/v1/campaigns/${campaignId}/sender`, CampaignDetailSchema, {
    method: 'PUT',
    body: { senderIdentityId },
  });
}

// ── The lifecycle (spec § 4.1) ───────────────────────────────────────────────
//
// Every one of these endpoints existed server-side, tested, with no client function to call it —
// which is why none of the states between Draft and Sent were reachable.

/**
 * The approval history, newest first — including invalidated ones.
 *
 * Invalidated rows are kept and returned rather than deleted, because "this was approved and then
 * edited" is exactly what somebody auditing a send needs to see. So the UI has to render them too.
 */
const ApprovalSchema = z.object({
  id: z.string().uuid(),
  approverSubject: z.string(),
  approverEmail: z.string(),
  requestedAt: z.string(),
  isApproved: z.boolean().nullable(),
  decidedAt: z.string().nullable(),
  comment: z.string().nullable(),
  invalidatedAt: z.string().nullable(),
  invalidationReason: z.string().nullable(),
});

export type Approval = z.infer<typeof ApprovalSchema>;

export async function listApprovals(campaignId: string) {
  return request(`/api/v1/campaigns/${campaignId}/approvals`, z.array(ApprovalSchema));
}

export async function requestApproval(
  campaignId: string,
  body: { approverSubject: string; approverEmail: string; comment: string | null },
) {
  return requestVoid(`/api/v1/campaigns/${campaignId}/request-approval`, { method: 'POST', body });
}

export async function approveCampaign(campaignId: string, comment: string | null) {
  return requestVoid(`/api/v1/campaigns/${campaignId}/approve`, { method: 'POST', body: { comment } });
}

export async function rejectCampaign(campaignId: string, comment: string | null) {
  return requestVoid(`/api/v1/campaigns/${campaignId}/reject`, { method: 'POST', body: { comment } });
}

/** `at` is an ISO instant. Our scheduler holds the campaign; SendGrid's send_at caps at 72h (§ 5.5). */
export async function scheduleCampaign(campaignId: string, at: string) {
  return request(`/api/v1/campaigns/${campaignId}/schedule`, CampaignDetailSchema, {
    method: 'POST',
    body: { at },
  });
}

export async function unscheduleCampaign(campaignId: string) {
  return request(`/api/v1/campaigns/${campaignId}/unschedule`, CampaignDetailSchema, {
    method: 'POST',
  });
}

/**
 * Pause and cancel report how many recipients were genuinely still stoppable and how many are past
 * recall. That second number is the whole point: "paused" without it is the answer that gets somebody
 * in trouble, because a chunk already handed to the transport is gone.
 */
const PauseOutcomeSchema = z.object({
  paused: z.boolean(),
  transportBatchPaused: z.boolean(),
  stoppableRecipients: z.number().int(),
  alreadyCommittedRecipients: z.number().int(),
  regime: z.string(),
});

export type PauseOutcome = z.infer<typeof PauseOutcomeSchema>;

const CancelOutcomeSchema = z.object({
  cancelled: z.boolean(),
  transportBatchCancelled: z.boolean(),
  stoppedRecipients: z.number().int(),
  alreadyCommittedRecipients: z.number().int(),
});

export type CancelOutcome = z.infer<typeof CancelOutcomeSchema>;

export async function pauseCampaign(campaignId: string, reason: string) {
  return request(`/api/v1/campaigns/${campaignId}/pause`, PauseOutcomeSchema, {
    method: 'POST',
    body: { reason },
  });
}

export async function resumeCampaign(campaignId: string) {
  return request(`/api/v1/campaigns/${campaignId}/resume`, CampaignDetailSchema, { method: 'POST' });
}

export async function cancelCampaign(campaignId: string, reason: string) {
  return request(`/api/v1/campaigns/${campaignId}/cancel`, CancelOutcomeSchema, {
    method: 'POST',
    body: { reason },
  });
}

// ── Reporting, the checklist and the send flow ───────────────────────────────

export async function getChecklist(campaignId: string): Promise<PreSendChecklist> {
  return request(`/api/v1/campaigns/${campaignId}/checklist`, PreSendChecklistSchema);
}

const CampaignLinkPageSchema = pagedSchema(CampaignLinkSchema);

/**
 * Every link in the campaign, in document order.
 *
 * Separate from the report's `topLinks`, which ranks by clicks and caps at 50 — that loses the order
 * a reader met them in and drops the links nobody clicked, which is the finding rather than an empty
 * row.
 */
export async function listCampaignLinks(campaignId: string, params: { take?: number; skip?: number }) {
  const search = new URLSearchParams();
  search.set('take', String(params.take ?? 100));
  search.set('skip', String(params.skip ?? 0));

  return request(`/api/v1/campaigns/${campaignId}/links?${search.toString()}`, CampaignLinkPageSchema);
}

export async function getReport(campaignId: string): Promise<CampaignReport> {
  return request(`/api/v1/campaigns/${campaignId}/report`, CampaignReportSchema);
}

/**
 * Enqueues the dispatch. Returns void because the interesting outcome is asynchronous — the job is
 * what sends, and the campaign's status is where progress shows up.
 */
export async function sendCampaign(campaignId: string): Promise<void> {
  return requestVoid(`/api/v1/campaigns/${campaignId}/send`, { method: 'POST' });
}

export async function sendTest(input: {
  campaignId: string;
  armId: string;
  toEmail: string;
}): Promise<{ accepted: boolean; error: string | null }> {
  return request(
    `/api/v1/campaigns/${input.campaignId}/arms/${input.armId}/test`,
    z.object({ accepted: z.boolean(), error: z.string().nullable() }),
    { method: 'POST', body: { toEmail: input.toEmail } },
  );
}
